import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import {
  useCreateConnectionAgentMutation,
  useCreateDraftMutation,
  useDeleteDraftMutation,
  useUpdateConnectionAgentMutation,
} from '../../../../../../services/api';
import agentData from '../../../../../../constants/agents/initialStates/agentData';
import { agentFormatOf } from '../../../../../../constants/agents/connectionAgent';
import { getEventWorkflowAgentsPath } from '../../../../../events/controllers/eventFormatters';
import useConnectionAgentValidator from './useConnectionAgentValidator';
import { useConnectionAgentEvent } from './useConnectionAgentDefineHandler';

const errorText = (error, fallback) => error?.data?.errorObject?.userErrorText || fallback;
export const connectionAgentPath = (agentId, tab) =>
  `${agentData.overviewPath}?_id=${agentId}&isConnection=true${tab ? `&tab=${tab}` : ''}`;

/**
 * The Connection setup's last step, three ways:
 * - a new setup: **Publish and run matching** creates the agent (and clears its draft, if it was one);
 * - editing an agent after it's made (`existing`): **Save changes** updates it (D4: nothing in the app is taken away);
 * - **Save as draft** keeps the answers, and the event they belong to, to continue later.
 */
const useConnectionAgentPublishHandler = () => {
  const navigate = useNavigate();
  const search = useLocation().search;
  const agentState = useSelector(state => state.agent);
  const connectionAgent = agentState.connectionAgent;
  const playbookId = agentState.data.playbookId;
  const creationType = agentState.etc.creationType;
  const { delegateCount, sponsorCount, delegatesToMatch } = useConnectionAgentValidator();
  const [CREATE_AGENT, { isLoading: isPublishing }] = useCreateConnectionAgentMutation();
  const [UPDATE_AGENT, { isLoading: isSaving }] = useUpdateConnectionAgentMutation();
  const [CREATE_DRAFT, { isLoading: isSavingDraft }] = useCreateDraftMutation();
  const [DELETE_DRAFT] = useDeleteDraftMutation();

  const {
    title,
    formats: slots,
    sessionsCollectionId,
    sessionsCollectionTitle,
    attendeeCollectionId,
    attendeeCollectionTitle,
    existing,
  } = connectionAgent;
  // The API takes the slot's format names ('session_recs'), never the engine's ('sessions').
  const formats = (slots || []).map(slot => ({ ...slot, format: agentFormatOf(slot.format) }));
  const isEditing = Boolean(existing?.agentId);
  const draftId = creationType === 'draft' ? new URLSearchParams(search).get('_id') : null;
  // A live agent needs its event (QA round 3, E1): the Define step asks for it when none is open.
  const { eventId, eventName } = useConnectionAgentEvent();
  const isPublishDisabled = Boolean(
    !eventId ||
      !title?.trim() ||
      !formats.length ||
      formats.some(slot => !slot.engineId) ||
      !attendeeCollectionId ||
      !delegatesToMatch
  );

  const handlePublish = async () => {
    if (isPublishDisabled) {
      toast.warning('Finish the earlier steps first', {
        description:
          'Pick the event, add a name, at least one format with its engine, and the attendee collection.',
      });
      return;
    }
    try {
      const response = await CREATE_AGENT({
        title: title.trim(),
        eventId,
        playbookId: playbookId || null,
        formats,
        sessionsCollectionId,
        sessionsCollectionTitle,
        attendeeCollectionId,
        attendeeCollectionTitle,
        rules: connectionAgent.rules,
        meetings: connectionAgent.meetings,
      }).unwrap();
      const newAgentId = response?.data?.id;
      if (!newAgentId) {
        toast.error('Couldn’t publish the agent', { description: 'Try publishing again.' });
        return;
      }
      // Made from a draft: the draft has done its job.
      if (draftId) await DELETE_DRAFT(draftId);
      toast.success('Agent published', {
        description:
          formats.length > 1
            ? 'Matching has started: one format at a time, in the order you chose. Review the results, then publish them to the app.'
            : 'The first matching run has started. Review the results, then publish them to the app.',
      });
      navigate(connectionAgentPath(newAgentId));
    } catch (error) {
      toast.error('Couldn’t publish the agent', {
        description: errorText(error, 'Try publishing again.'),
      });
    }
  };

  const handleSaveChanges = async () => {
    if (isPublishDisabled) {
      toast.warning('Finish the setup first', {
        description:
          'It needs its event, a name, at least one format with its engine, and attendees.',
      });
      return;
    }
    try {
      const response = await UPDATE_AGENT({
        agentId: existing.agentId,
        // The API only takes it for an agent saved without its event; others keep theirs.
        eventId: eventId || undefined,
        title: title.trim(),
        formats,
        sessionsCollectionId,
        sessionsCollectionTitle,
        // Another attendee collection adds the people in it who aren't in the agent yet (D4: nobody is removed).
        attendeeCollectionId,
        attendeeCollectionTitle,
        rules: connectionAgent.rules,
        meetings: connectionAgent.meetings,
      }).unwrap();
      const added = response?.data?.added ?? 0;
      toast.success('Setup saved', {
        description: added
          ? `${added.toLocaleString()} ${added === 1 ? 'person was' : 'people were'} added. Run matching for people without suggestions to give them some.`
          : 'New runs use it from now on. Nothing already in the app changes.',
      });
      navigate(connectionAgentPath(existing.agentId));
    } catch (error) {
      toast.error('Couldn’t save the setup', {
        description: errorText(error, 'Try again in a moment.'),
      });
    }
  };

  const handleSaveDraft = async () => {
    try {
      // The whole setup, and the event it belongs to, so Continue setup picks up where it stopped.
      const draft = {
        ...agentState,
        connectionAgent: {
          ...connectionAgent,
          eventId,
          eventName: eventName || connectionAgent.eventName || null,
        },
      };
      const response = await CREATE_DRAFT({
        draftType: agentData.draft?.value || 'campaign',
        draftString: JSON.stringify(draft),
      }).unwrap();
      if (!response?.success) throw new Error('not saved');
      if (draftId) await DELETE_DRAFT(draftId);
      toast.success('Setup saved as a draft', {
        description: 'Nothing runs and nothing reaches attendees until you publish it.',
      });
      // The event's Connections agents list the draft; without an event, stay on the draft itself
      // (the Agents list shows it too).
      const savedId = response?.data?._id;
      if (eventId) navigate(getEventWorkflowAgentsPath(eventId, 'connections'));
      else if (savedId) navigate(`${agentData.draftPath}?_id=${savedId}`, { replace: true });
      else navigate('/agents/dashboard');
    } catch (error) {
      toast.error('Couldn’t save the draft', {
        description: errorText(error, 'Try again in a moment.'),
      });
    }
  };

  return {
    isEditing,
    isPublishing,
    isSaving,
    isSavingDraft,
    isPublishDisabled,
    eventName,
    delegateCount,
    sponsorCount,
    handlePublish,
    handleSaveChanges,
    handleSaveDraft,
  };
};

export default useConnectionAgentPublishHandler;
